/* eslint-disable max-lines */
import { Info, ModeCommentOutlined } from '@mui/icons-material'
import { Stack, Tooltip, Typography } from '@mui/material'
import {
  getGridBooleanOperators,
  getGridSingleSelectOperators,
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid-premium'
import { isThisHour } from 'date-fns'
import isNil from 'lodash/isNil'
import isNull from 'lodash/isNull'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormatter } from '@omnea-app/fe-i18n/useFormatter'
import { usePriceFormatter } from '@omnea-app/fe-i18n/usePriceFormatter'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { useRequestRiskColumns } from '@omnea-app/fe-risks/columns/useRequestRiskColumns'
import { getFormattedIsoDateValueCol } from '@omnea-app/fe-tables/column-formatters/date/getFormattedIsoDateValueCol'
import { useColumnConfig } from '@omnea-app/fe-tables/columns/useColumnConfig'
import { getGridAutocompleteFilterOperator } from '@omnea-app/fe-tables/ControlledTable/components/AutocompleteInputFilter'
import { EditableTextCell } from '@omnea-app/fe-tables/data-grid/EditableTextCell'
import { TextInputCell } from '@omnea-app/fe-tables/data-grid/TextInputCell'
import { applyBackendColumnCapabilities } from '@omnea-app/fe-tables/data-grid/utils/applyBackendColumnCapabilities'
import { RowGroupingColumnTableCell } from '@omnea-app/fe-tables/row-grouping/components/RowGroupingColumnTableCell'
import { getRowGroupingColumnTableCellValue } from '@omnea-app/fe-tables/row-grouping/helpers'
import {
  applyDefaultColumnDefinitionsToColumns,
  DEFAULT_MINIMUM_LEFT_PINNED_COLUMN_WIDTH,
} from '@omnea-app/fe-tables/utils/columnDefaults'
import { getCommentAuthorName } from '@omnea-app/fe-users-and-groups/getCommentAuthorName'
import { PriceDisplay } from '@omnea-app/frontend/components/shared/ui/price/PriceDisplay'
import { ProfileBox } from '@omnea-app/frontend/components/shared/ui/profile-box/ProfileBox'
import { formatDuration } from '@omnea-app/frontend/utils/duration/formatDuration'
import { htmlToMarkdown } from '@omnea-app/frontend/utils/string/htmlToMarkdown'
import { parseDateString } from '@omnea-app/packages/universal/date-utils/parseDateString'
import { AssigneeTypeEnum, PaymentIntervalEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { getAnnualPriceValue } from '@omnea-app/packages/universal/price/getAnnualPriceValue'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { Price } from '@omnea-app/requests/price'
import { REQUESTS_FILTER_CAPABILITIES } from '@omnea-app/requests/requestsFrontendClient'
import { CommentStringWithMentions } from '../detail/components/comments/comments-list/CommentStringWithMentions'
import { RequestOwner } from '../detail/components/owners/RequestOwner'
import { RequestPriorityController } from '../priority/RequestPriorityController'
import { requestPriorityEnumDataGridComparator } from '../priority/utils'
import { getInitialColumnVisibility, priceFormResponseKeys } from '../RequestsTable/helpers/useRequestsTableDefaultState'
import { getStageValueTranslationString } from './getStageValueTranslationString'
import { useRemotePOColumns } from './hooks/useRemotePOColumns'
import { RequestStage } from './RequestStage'
import { RequestRow } from './useRequestTableRows'

interface RequestColumnsConfigs {
  editable?: boolean
  sortable?: boolean
  hiddenColumns?: string[]
  // When the table runs on the server-side pagination path, gate sort/filter/group to what the
  // backend supports; unsupported columns must never send an operation v2 would silently drop.
  backendPaginationEnabled?: boolean
}

const TextCell = ({ value }: { value: string | null }) => {
  return <Typography sx={{ color: 'text.secondary' }}>{value}</Typography>
}

export const useRequestColumns = ({
  editable = true,
  sortable = true,
  hiddenColumns = [],
  backendPaginationEnabled = false,
}: RequestColumnsConfigs): {
  columns: GridColDef<RequestRow>[]
  initialColumnVisibility: Record<string, boolean>
} => {
  const { t } = useTranslation('requests')
  const { t: tShared } = useTranslation('shared')
  const { t: tCommon } = useTranslation('common')
  const { PURCHASE_AGREEMENTS, ENABLE_REQUEST_LEADS, SHOW_CATEGORY_PATH_IN_REQUEST_TABLE } = useFeatureFlags()
  const riskColumns = useRequestRiskColumns<RequestRow>({ sortable })
  const remotePOColumns = useRemotePOColumns({ sortable })
  const { formatDate } = useFormatter()
  const { getAmountInDefaultCurrency } = usePriceFormatter({})

  // Stable identities for the groupingValueGetter functions (which often fall back to valueGetter) are required to avoid grouping
  // Randomly expanding/collapsing when the table rerenders in particular circumstances (e.g. when a row is opened in a new tab)
  // Not memoising the whole columnConfig because it would be too fragile and easy to break

  const defaultColumns = useColumnConfig<RequestRow>({
    hasSupplierColumn: true,
    columnConfig: [
      {
        columnType: 'text',
        field: 'publicId',
        sortable,
        headerName: t('table-column.requestId'),
        filterOperators: getGridAutocompleteFilterOperator(),
      },
      {
        columnType: 'custom-render',
        field: 'priority',
        headerName: t('table-column.priority'),
        editable,
        sortable,
        filterOperators: getGridAutocompleteFilterOperator(),
        renderCell: (params) => {
          if (params.rowNode.type === 'group') {
            const rowGroupingColumnTableCellValue = getRowGroupingColumnTableCellValue(params.value)
            if (isNull(rowGroupingColumnTableCellValue)) return null
            return (
              <RowGroupingColumnTableCell
                columnHeaderName={t('table-column.priority')}
                value={rowGroupingColumnTableCellValue}
                id={params.id}
              />
            )
          }
          return (
            <RequestPriorityController
              requestId={params.row.id}
              priority={params.value}
              displayBorderOnHover
              readonly={!editable}
            />
          )
        },
        sortComparator: requestPriorityEnumDataGridComparator,
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.priority, []),
        valueFormatter: (_, row) => row.priority,
      },
      {
        columnType: 'custom-editable',
        field: 'name',
        headerName: t('table-column.name'),
        hideable: false,
        sortable,
        style: {
          minWidth: DEFAULT_MINIMUM_LEFT_PINNED_COLUMN_WIDTH,
        },
        renderCell: (params) => <EditableTextCell {...params} disabled={!editable} />,
        renderEditCell: (params) => <TextInputCell isFirst {...params} />,
      },
      {
        columnType: 'supplier',
        headerName: t('table-column.suppliers'),
        field: 'supplier',
        sortable,
      },
      {
        columnType: 'chip-multiple',
        field: 'tags',
        headerName: t('table-column.tags'),
        filterOperators: getGridAutocompleteFilterOperator(),
        valueGetter: useCallback(
          (_: unknown, row) => ('tags' in row && row.tags != null ? row.tags.map((value: string) => value).join(', ') : null),
          []
        ),
        getItems: (params) => params.row?.tags.map((name) => ({ name })) ?? [],
      },
      {
        columnType: 'text',
        filterOperators: getGridAutocompleteFilterOperator(),
        headerName: t('table-column.workflowName'),
        field: 'workflowTitle',
        sortable,
        groupingValueGetter: useCallback((_: unknown, row: { workflowTitle: string }) => row.workflowTitle, []),
      },
      {
        columnType: 'custom-render',
        field: 'stage',
        sortable,
        headerName: t('table-column.stage'),
        filterOperators: getGridAutocompleteFilterOperator(),
        valueGetter: useCallback(
          (_: unknown, row: RequestRow) => {
            const value = getStageValueTranslationString({ row, t: tShared })
            return value?.currentStepsJoined ?? value.firstItem
          },
          [tShared]
        ),
        renderCell: (params) => {
          const stageValue = getStageValueTranslationString({ row: params.row, t: tShared }).firstItem
          return <RequestStage row={params.row} stageValue={stageValue} />
        },
      },
      {
        columnType: 'assignees',
        sortable,
        field: 'allCurrentAssignees',
        headerName: t('table-column.assignees'),
        valueGetter: useCallback(
          (_: unknown, row: RequestRow) =>
            [
              ...(row.currentUserAssignees ?? []).map((u) => u.name),
              ...(row.currentGroupAssignees ?? []).map((g) => g.name),
            ].join(', '),
          []
        ),
        getAssignees: (params) => {
          const assignees = [
            ...(params.row.currentUserAssignees ?? []).map((u) => ({
              id: u.id,
              name: u.name,
              value: u.id,
              type: AssigneeTypeEnum.USER,
            })),
            ...(params.row.currentGroupAssignees ?? []).map((g) => ({
              id: g.id,
              name: g.name,
              value: g.id,
              type: AssigneeTypeEnum.GROUP,
            })),
          ]
          return assignees
        },
      },
      {
        columnType: 'date',
        headerName: tCommon('detail.fields.created-at.label'),
        field: 'createdAt',
        sortable,
      },
      {
        columnType: 'assignees',
        field: 'createdBy',
        sortable,
        headerName: tCommon('detail.fields.created-by.label'),
        valueGetter: useCallback(
          (_: unknown, row: RequestRow) =>
            row.requestor != null ? `${row.requestor.firstName} ${row.requestor.lastName}` : null,
          []
        ),
        getAssignees: (params) =>
          params.row.requestor != null
            ? [
                {
                  id: params.row.requestor.id,
                  name: `${params.row.requestor.firstName} ${params.row.requestor.lastName}`,
                  email: params.row.requestor.email,
                  value: params.row.requestor.email,
                  type: AssigneeTypeEnum.USER,
                },
              ]
            : [],
      },
      {
        columnType: 'chip-single',
        field: 'status',
        sortable,
        headerName: t('table-column.status.title'),
        filterOperators: getGridAutocompleteFilterOperator(),
        valueGetter: useCallback(
          (_: unknown, row: RequestRow) => (row.status ? t(`table-column.status.${row.status}`) : null),
          [t]
        ),
      },
      {
        columnType: 'custom-render',
        field: 'estimatedCompletionTime',
        sortable,
        headerName: t('table-column.dueDate'),
        type: 'dateTime',
        valueFormatter: getFormattedIsoDateValueCol,
        valueGetter: useCallback((_: unknown, row: RequestRow) => {
          /*
           * If the request has been completed, we should parse and return the `completedAt` date `string`.
           * Otherwise, if the request is pending, we should use the `estimatedCompletionTime` date property,
           * which will either be a valid `Date` object, or `null`.
           */
          return row.completedAt != null ? parseDateString(row.completedAt) : row.estimatedCompletionTime
        }, []),
        renderCell: (params) => {
          const dueDate = params.row.estimatedCompletionTime
            ? formatDate(params.row.estimatedCompletionTime, { format: 'short' })
            : ''
          const isDueDateNow = params.row.estimatedCompletionTime && isThisHour(params.row.estimatedCompletionTime)
          return (
            <>
              <Typography sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>{dueDate}</Typography>
              {isDueDateNow && (
                <Tooltip title={t('action-required-for-last-step')}>
                  <Info sx={{ color: 'fg.subtle', fontSize: 16, ml: 1 }} />
                </Tooltip>
              )}
            </>
          )
        },
      },
      {
        columnType: 'text',
        field: 'currency',
        headerName: t('table-column.currency'),
        filterOperators: getGridSingleSelectOperators(),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.value?.currency, []),
      },
      {
        columnType: 'custom-render',
        field: 'projectOwner',
        sortable,
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.projectOwner?.name ?? null, []),
        headerName: t('table-column.owner'),
        filterOperators: getGridAutocompleteFilterOperator('name'),
        renderCell: (params) => {
          if (params.rowNode.type === 'group') {
            return <RowGroupingColumnTableCell columnHeaderName={t('table-column.owner')} value={params.value} id={params.id} />
          }
          const owner = params.row.projectOwner
            ? {
                id: params.row.projectOwner.id,
                name: params.row.projectOwner.name,
              }
            : null
          return <RequestOwner requestId={params.row.id} owner={owner} variant="REQUEST_TABLE_COLUMN" />
        },
      },
      {
        columnType: 'custom-render',
        field: 'annualValue',
        type: 'number',
        sortable,
        headerName: t('table-column.annualValue'),
        valueGetter: useCallback(
          (_: unknown, row: RequestRow) => {
            if (!row.value) return null
            const valueInDefaultCurrency = getAmountInDefaultCurrency(row.value as Price)
            if (valueInDefaultCurrency == null) return null
            const multiYears = row.value?.multiYears ?? null
            return getAnnualPriceValue(
              valueInDefaultCurrency,
              row.value.paymentInterval as PaymentIntervalEnum | undefined,
              multiYears
            )
          },
          [getAmountInDefaultCurrency]
        ),
        renderCell: (params) => {
          const value = params.row.value
          if (isNil(value)) return null
          const paymentInterval = value.paymentInterval

          const originalAmount = Number(value.amount)
          const multiYears = params.row.value?.multiYears ?? null
          return (
            <PriceDisplay
              price={{
                ...value,
                paymentInterval: paymentInterval as PaymentIntervalEnum | undefined,
                amount: getAnnualPriceValue(originalAmount, paymentInterval as PaymentIntervalEnum | undefined, multiYears),
              }}
              hideFrequency
              direction="column"
            />
          )
        },
      },
      {
        columnType: 'custom-render',
        field: 'budget',
        sortable,
        headerName: t('table-column.budget'),
        filterOperators: getGridAutocompleteFilterOperator('name'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.budget?.name ?? null, []),
        renderCell: (params) => {
          if (params.rowNode.type === 'group') {
            return (
              <RowGroupingColumnTableCell
                columnHeaderName={t('table-column.budget')}
                value={params.value || 'N/A'}
                id={params.id}
              />
            )
          }
          if (params.value == null) return null
          return <Typography>{params.value}</Typography>
        },
      },
      {
        columnType: 'chip-multiple',
        field: 'autoGeneratedTags',
        headerName: t('table-column.auto-generated-tags'),
        valueGetter: useCallback(
          (_: unknown, row) =>
            'autoGeneratedTags' in row ? (row.autoGeneratedTags ?? []).map((value: string) => value).join(', ') : '',
          []
        ),
        getItems: (params) => params.row.autoGeneratedTags?.map((name) => ({ name })) ?? [],
      },
      {
        columnType: 'date',
        headerName: t('table-column.completed-at'),
        field: 'completedAt',
        sortable,
      },
      {
        columnType: 'custom-render',
        type: 'number',
        field: 'value',
        sortable,
        headerName: t('table-column.value'),
        valueGetter: useCallback(
          (_: unknown, row: RequestRow) => {
            if (!row.value) return null
            return getAmountInDefaultCurrency(row.value as Price)
          },
          [getAmountInDefaultCurrency]
        ),
        renderCell: (params) => {
          const value = params.row.value
          if (isNil(value)) return null
          return (
            <PriceDisplay
              price={{
                ...value,
                paymentInterval: value.paymentInterval as PaymentIntervalEnum | undefined,
                amount: Number(value.amount),
              }}
              direction="column"
            />
          )
        },
      },
      {
        columnType: 'date',
        headerName: t('table-column.renewalDate'),
        field: 'renewalDate',
      },
      ...riskColumns,
      {
        field: 'purchaseOrderNumber',
        sortable,
        headerName: PURCHASE_AGREEMENTS ? t('table-column.po-pa-number') : t('table-column.po-number'),
        editable,
        columnType: 'custom-render',
        hideable: false,
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.poNumber, []),
        renderCell: (params) => {
          return <TextCell value={params.row?.poNumber ?? ''} />
        },
      },
      {
        columnType: 'custom-render',
        field: 'latestComment',
        headerName: t('table-column.last-comment'),
        editable,
        valueGetter: useCallback(
          (_: unknown, row: RequestRow) => {
            const comment = row.latestComment
            if (!comment) return null

            return `${formatDate(new Date(comment.createdAt), { format: 'long' })}: ${htmlToMarkdown(comment.text)}`
          },
          [formatDate]
        ),
        renderCell: (params) => {
          const comment = params.row.latestComment
          if (!comment) {
            return null
          }
          const authorName = getCommentAuthorName(comment)
          return (
            <Tooltip
              title={
                <Stack>
                  <Stack direction="row" gap={4} alignItems="center">
                    <ProfileBox
                      text={authorName}
                      userId={comment.user?.id ?? null}
                      avatarVariant="circular"
                      textProps={{ color: 'text.inverted', fontWeight: 500 }}
                    />
                    <Typography variant="small-medium" sx={{ color: 'text.inverted' }}>
                      {formatDate(new Date(comment.createdAt), { format: 'medium' })}
                    </Typography>
                  </Stack>
                  <CommentStringWithMentions commentText={comment.text} mentionedUsers={comment.mentions} variant="MainBody" />
                </Stack>
              }
              slotProps={{
                tooltip: {
                  sx: ({ palette }) => ({
                    p: 3,
                    '& p': { color: 'text.inverted' },
                    '& .comment-mention': {
                      color: `${palette.primary.light} !important`,
                      background: 'unset !important',
                    },
                  }),
                },
              }}
            >
              <ModeCommentOutlined sx={{ fontSize: 22, color: 'fg.subtle' }} />
            </Tooltip>
          )
        },
      },
      {
        columnType: 'text',
        field: 'productService',
        headerName: t('table-column.productService'),
        valueGetter: useCallback(
          (_: unknown, row: RequestRow) => (isDefined(row.product) && 'name' in row.product ? row.product.name : null),
          []
        ),
      },
      ...useMemo(
        () =>
          priceFormResponseKeys.map((key) => ({
            field: key,
            headerName: t(`table-column.${key}`),
            sortable,
            type: 'number' as const,
            columnType: 'custom-render' as const,
            valueGetter: (_: unknown, row: RequestRow) => {
              const value = row[key]
              if (isNil(value)) return null
              const amountInDefaultCurrency = getAmountInDefaultCurrency({ ...value, amount: Number(value.amount) })
              if (amountInDefaultCurrency == null) return null
              const multiYears = row.value?.multiYears ?? null
              return getAnnualPriceValue(amountInDefaultCurrency, value.paymentInterval, multiYears)
            },
            renderCell: (params: GridRenderCellParams<RequestRow, string, any, GridTreeNodeWithRender>) => {
              const value = params.row[key]
              if (isNil(value)) return null
              const paymentInterval = value.paymentInterval
              const originalAmount = Number(value.amount)
              const multiYears = params.row.value?.multiYears ?? null
              return (
                <PriceDisplay
                  price={{
                    ...value,
                    amount: getAnnualPriceValue(originalAmount, paymentInterval, multiYears),
                  }}
                  hideFrequency
                  direction="column"
                />
              )
            },
          })),
        [sortable, t, getAmountInDefaultCurrency]
      ),
      {
        columnType: 'custom-render',
        field: 'department',
        sortable,
        headerName: t('table-column.department'),
        filterOperators: getGridAutocompleteFilterOperator('name'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.department?.name ?? '', []),
        renderCell: (params: GridRenderCellParams<RequestRow, any, any, GridTreeNodeWithRender>) => {
          if (params.rowNode.type === 'group') {
            return (
              <RowGroupingColumnTableCell columnHeaderName={t('table-column.department')} value={params.value} id={params.id} />
            )
          }
          if (isNil(params.value)) return null
          return <Typography sx={{ color: 'text.secondary' }}>{params.value}</Typography>
        },
      },
      {
        columnType: 'text',
        field: 'type',
        headerName: t('table-column.type'),
        filterOperators: getGridAutocompleteFilterOperator(),
      },
      {
        columnType: 'text',
        field: 'costCenter',
        headerName: t('table-column.costCenter'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.costCenter?.name, []),
      },
      {
        columnType: 'date',
        field: 'goLiveDate',
        headerName: t('table-column.goLiveDate'),
      },
      {
        columnType: 'text',
        field: 'budgetHolder',
        headerName: t('table-column.budgetHolder'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.budgetHolder?.name, []),
      },
      {
        columnType: 'text',
        field: 'businessOwner',
        headerName: t('table-column.businessOwner'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.businessOwner?.name, []),
      },
      {
        columnType: 'custom-render',
        field: 'autoRenewal',
        headerName: t('table-column.autoRenewal'),
        filterOperators: getGridBooleanOperators(),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.autoRenewal, []),
        valueFormatter: (value) => {
          if (isNil(value)) {
            return ''
          }
          return value ? tCommon('yes') : tCommon('no')
        },
        renderCell: (params) => {
          if (isNil(params.row?.autoRenewal)) {
            return <TextCell value="" />
          }
          return <TextCell value={params.row?.autoRenewal ? tCommon('yes') : tCommon('no')} />
        },
      },
      {
        columnType: 'custom-render',
        field: 'renewal',
        headerName: t('table-column.renewal'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.shouldRenew, []),
        valueFormatter: (value) => {
          if (isNil(value)) {
            return ''
          }
          return value ? tCommon('yes') : tCommon('no')
        },
        renderCell: (params) => {
          return <TextCell value={params.row?.shouldRenew ? tCommon('yes') : tCommon('no')} />
        },
      },
      {
        columnType: 'custom-render',
        field: 'duration',
        headerName: t('table-column.duration'),
        sortable,
        renderCell: (params) => {
          if (params.rowNode.type === 'group') return null

          if (isNil(params.value)) return null

          return <Typography>{formatDuration(params.value, tShared)}</Typography>
        },
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.duration, []),
        valueFormatter: (_, row) => (!isNil(row.duration) ? formatDuration(row.duration, tShared) : ''),
        sortComparator: (v1, v2) => {
          // Convert duration objects to total minutes for comparison
          if (isNil(v1) && isNil(v2)) return 0
          if (isNil(v1)) return -1
          if (isNil(v2)) return 1

          const totalMinutes1 = v1.days * 24 * 60 + v1.hours * 60 + v1.minutes
          const totalMinutes2 = v2.days * 24 * 60 + v2.hours * 60 + v2.minutes

          return totalMinutes1 - totalMinutes2
        },
      },
      {
        columnType: 'date',
        field: 'cancelBy',
        headerName: t('table-column.cancelBy'),
      },
      {
        columnType: 'date',
        field: 'contractStartDate',
        sortable,
        headerName: t('table-column.contractStartDate'),
      },
      {
        columnType: 'date',
        field: 'contractEndDate',
        sortable,
        headerName: t('table-column.contractEndDate'),
      },
      {
        columnType: 'text',
        field: 'buyerLegalEntity',
        headerName: t('table-column.buyerLegalEntity'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.buyerLegalEntity?.name, []),
      },
      {
        columnType: 'text',
        field: 'paymentTerm',
        headerName: t('table-column.paymentTerm'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.paymentTerm?.name, []),
      },
      {
        columnType: 'text',
        field: 'paymentMethod',
        headerName: t('table-column.paymentMethod'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.paymentMethod?.name, []),
      },
      {
        columnType: 'number',
        field: 'cancellationNotice',
        headerName: t('table-column.cancellationNotice'),
      },
      {
        columnType: 'custom-render',
        field: 'inBudget',
        headerName: t('table-column.inBudget'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.inBudget, []),
        renderCell: (params) => {
          return <TextCell value={params.row?.inBudget != null ? (params.row.inBudget ? tCommon('yes') : tCommon('no')) : ''} />
        },
      },
      {
        columnType: 'text',
        field: 'note',
        headerName: t('table-column.note'),
      },
      {
        columnType: 'text',
        field: 'category',
        headerName: t('table-column.category'),
        valueGetter: useCallback(
          (_: unknown, row: RequestRow) => (SHOW_CATEGORY_PATH_IN_REQUEST_TABLE ? row.categoryPath : row.category),
          [SHOW_CATEGORY_PATH_IN_REQUEST_TABLE]
        ),
      },
      {
        columnType: 'text',
        field: 'market',
        headerName: t('table-column.market'),
      },
      ...useMemo(
        () =>
          ENABLE_REQUEST_LEADS
            ? ([
                {
                  columnType: 'assignees' as const,
                  field: 'leads' as const,
                  sortable,
                  headerName: t('table-column.leads'),
                  valueGetter: (_: unknown, row: RequestRow) => row.leads?.map((lead) => lead.name).join(', '),
                  getAssignees: (params: GridRenderCellParams<RequestRow>) =>
                    params.row.leads?.map((lead) => ({
                      id: lead.id,
                      name: lead.name,
                      value: lead.id,
                      type: AssigneeTypeEnum.USER,
                    })),
                },
              ] as const)
            : [],
        [ENABLE_REQUEST_LEADS, sortable, t]
      ),
      {
        columnType: 'assignees',
        field: 'followers',
        sortable,
        headerName: t('table-column.followers'),
        valueGetter: useCallback((_: unknown, row: RequestRow) => row.followers?.map((follower) => follower.name).join(', '), []),
        getAssignees: (params) =>
          params.row.followers?.map((follower) => ({
            id: follower.id,
            name: follower.name,
            value: follower.id,
            type: AssigneeTypeEnum.USER,
          })),
      },
      ...remotePOColumns,
    ],
  })

  const columns = applyDefaultColumnDefinitionsToColumns(defaultColumns)

  return {
    columns: backendPaginationEnabled ? applyBackendColumnCapabilities(columns, REQUESTS_FILTER_CAPABILITIES) : columns,
    initialColumnVisibility: getInitialColumnVisibility(hiddenColumns),
  }
}
